/**
 * DEFUSE S10 — the bulged-polyline explode (@codex #431 P2 rounds 1+5; heavy
 * `POLYLINE` arm @codex round 17, FINDING B): a bulged polyline child is
 * decomposed into straight runs plus true curved Arc/Ellipse pieces before the
 * per-kind transform, because a single `bulge` scalar cannot express the
 * ELLIPSE a non-uniform INSERT scale makes of a circular arc.
 *
 * Split out of `./blockTransform.ts` (which re-exports the public surface and
 * carries the family's module documentation, including the chord-gap rationale
 * and the OSNAP-INTERSECT-EMIT-S2 follow-up) purely for file size — the
 * decomposition is unchanged. The bulge → CCW-arc conversion it used
 * `./blockInsertTransform.normalizeRad` for is the shared `bulgeArcCcwAngles`
 * one-truth in `drawing-model/bulgeArc.ts` (@codex PR #785 round 14, FINDING 2 —
 * the top-level heavy POLYLINE explode needs the identical swap from
 * `command-engine`, which cannot reach `apps/web`).
 *
 * # ONE decomposition, two source kinds (@codex round 17, FINDING B)
 *
 * {@link explodeBulgedSpans} is the span walk itself: maximal straight runs are
 * handed back to the SOURCE kind to rebuild (so an LWPOLYLINE run stays an
 * LWPOLYLINE and a heavy POLYLINE run stays a heavy POLYLINE, with its widths,
 * its group-39 thickness, its group-50 tangents and its per-record handles
 * intact), while every bulged span becomes the same Arc / Ellipse piece for
 * both. A second copy of the walk is how one kind ends up bowing a span to the
 * other side of its chord — which is the very bug `bulgeArcCcwAngles` was
 * hoisted into `drawing-model` to prevent one level down.
 *
 * # WHEN the heavy POLYLINE takes it, and why not always
 *
 * The LWPOLYLINE arm explodes under EVERY insert scale — its clone carries no
 * per-vertex field the pieces cannot hold, so the output is exact either way.
 * The heavy arm explodes only under a NON-SIMILARITY insert
 * ({@link isSimilarityTransform}), because under a similarity the authored
 * bulge is an EXACT description of the image (a similarity maps a circle to a
 * circle), so exploding there would trade a perfect circular arc for the loss
 * of the group-50 tangents and the per-record handles the pieces cannot carry —
 * a regression, not a fix. Under a non-similarity the bulge is simply wrong.
 *
 * # WHAT the curved piece becomes, and why it is not always an ELLIPSE
 *
 * @codex round 18, FINDING B. An `Ellipse` piece is the affine image of the
 * authored arc EXACTLY — but it is only the CENTRELINE. A heavy 2D `POLYLINE`
 * can also carry a per-vertex width BAND (groups 40/41) and a group-39
 * THICKNESS sweep, and `SceneGeometry.ellipse` has neither field, so converting
 * a wide or thick span to a bare `Ellipse` dropped both while the straight runs
 * beside it kept them: ONE entity drawn half as a band and half as a bare
 * centreline. So the piece kind follows WHAT THE SPAN CARRIES:
 *
 *   - a run whose walked spans declare NO width and whose entity declares no
 *     group-39 sweep — nothing to lose — is decomposed as before: straight runs
 *     plus true `Ellipse` curves, which keep their curve exact at every zoom
 *     and give the snaps a real ellipse to bite on;
 *   - a run that DOES carry a band or a sweep is instead REBUILT, whole, as one
 *     bulge-free heavy `POLYLINE` piece whose vertices are its own arcs already
 *     walked IN ITS OWN OCS PLANE ({@link walkedHeavyPolylineRun}). The kind is
 *     the one whose adapter paints both a band and a sweep, so both survive; the
 *     walk is the SHARED one (`cad-core/polylineWidthBand.ts` —
 *     `polylineSpanSubSegments` for the subdivision, `polylineBandSegments`,
 *     which is a `.map` over it, for the taper), so the sub-segments AND the
 *     group 40→41 values interpolated onto them are
 *     the very ones the band builder would have produced — the drawn band is
 *     bit-for-bit what it was, and the drawn CENTRELINE, the pick, the snaps and
 *     the plot now follow the true affine image instead of a circle through the
 *     mapped endpoints. Rebuilding the WHOLE run (rather than per-span pieces)
 *     is what keeps the band's mitred joints, which splitting an entity destroys.
 *
 * # The child's OWN OCS plane travels with the pieces (@codex round 18, FINDING A)
 *
 * A 2D heavy polyline with a NON-DEFAULT group 210 authors its vertices — and
 * its bulges — in the plane of that extrusion, so the arc must be built THERE
 * and lifted, never read off the already-lifted WCS x/y (the sentence
 * `cad-core/polylineWidthBand.ts::polylineCentrelineSamples` and
 * `../geom/polylinePlane.ts` both state). Both arms above do exactly that:
 * `polylinePlane(child.extrusion)` projects the resolved points back into the
 * authored plane, the walk happens there, and the `Ellipse` piece is lifted out
 * of it — centre, major axis and group 210 all — which is precisely the
 * completion `drawing-model/ocsPlanarTransform.ts`' header names for its own
 * residual "2. THE BOW" ("Explode-to-LINE+ELLIPSE … an ELLIPSE carries its own
 * plane and axes"). The tilted case therefore no longer opts OUT of the
 * decomposition; before this it kept its authored bulge and re-drew a CIRCLE
 * through the mapped endpoints, whose bow was wrong by the whole difference
 * between the two semi-axes.
 */
import {
  heavyPolylineRuns,
  heavyPolylineWcsResolver,
  type HeavyPolylineRun,
} from "@dxf-ai-editor/cad-core";
import {
  DXF_POLYLINE_FLAG,
  arcEntitySchema,
  bulgeArc,
  bulgeArcCcwAngles,
  computeOcsAxes,
  ellipseEntitySchema,
  hasPolylineWidth,
  heavyPolylineWidths,
  polylineSubtype,
} from "@dxf-ai-editor/drawing-model";
import type {
  BlockChildEntity,
  LwpolylineEntity,
  PolylineEntity,
  PolylineVertexRecord,
  PolylineWidth,
} from "@dxf-ai-editor/drawing-model";

import type { Vec3 } from "../../types";
import { polylinePlane, type PolylinePlane } from "../geom/polylinePlane";
import { thicknessSweepVector } from "../geom/polylineThicknessSweep";
import {
  polylineBandSegments,
  polylineSpanSubSegments,
} from "../geom/polylineWidthBand";

import { isSimilarityTransform } from "./blockChildBandFrame";
import type { BlockInsertTransform } from "./blockInsertTransform";

/* ── Bulge explode (@codex #431 P2 rounds 1+5) ──────────────────────── */

/**
 * Does this LWPOLYLINE child need the explode-before-transform treatment?
 * True whenever ANY vertex carries a bulge (@codex #431 round-5). The batcher
 * now tessellates bulges (`batchedLayer.ts` (`polylineCentrelineSamples`), @codex
 * PR #785 round 3 F1), so a carried bulge DOES draw as a curve — but only a
 * CIRCULAR one, and the affine image of a circular arc under a non-uniform
 * insert scale is an ELLIPSE, which no bulge scalar can encode. Exploding to
 * true Arc/Ellipse pieces stays exact under every scale (and is identical
 * output where the bulge would now suffice); bulge-free polylines keep the
 * single-renderable fast path.
 */
export function lwpolylineNeedsBulgeExplode(child: LwpolylineEntity): boolean {
  return child.vertices.some((v) => (v.bulge ?? 0) !== 0);
}

/**
 * Does this heavy `POLYLINE` child need the explode-before-transform treatment
 * (@codex round 17, FINDING B)?
 *
 * The two gates, in the order the module header argues them:
 *   - the 2D subtype only — group 42 is an `AcDb2dVertex` field, so a 3D
 *     polyline and both mesh subtypes have no bulge to explode (the shared
 *     topology pins theirs to 0 anyway, so this is the same answer stated
 *     cheaply);
 *   - a NON-SIMILARITY insert only — under a similarity the authored bulge is
 *     an exact description of the image and decomposing would lose the
 *     group-50 tangents and the per-record handles for nothing.
 *
 * A NON-DEFAULT group-210 extrusion is NOT a third gate (@codex round 18,
 * FINDING A). It used to be: the tilted child opted out and kept its bulge, so
 * a CIRCLE was re-drawn through the mapped endpoints whose bow was wrong by the
 * whole difference between the image's two semi-axes. The authored plane now
 * travels with the decomposition instead (module header), which is the
 * completion `ocsPlanarTransform.ts`' residual "2. THE BOW" names.
 *
 * The bulges are read off the SHARED topology (`heavyPolylineRuns`), not off the
 * raw record list, so a spline-fit polyline's control FRAME cannot decide this,
 * and only the spans that are actually WALKED are consulted — the last entry of
 * an OPEN run bows the wrap-around span, which an open run never draws.
 */
export function heavyPolylineNeedsBulgeExplode(
  child: PolylineEntity,
  t: BlockInsertTransform,
): boolean {
  if (polylineSubtype(child) !== "2d") return false;
  if (isSimilarityTransform(t)) return false;
  const run = heavyPolylineRuns(child)[0];
  if (!run) return false;
  const spans = run.closed ? run.indices.length : run.indices.length - 1;
  for (let i = 0; i < spans; i++) {
    if ((run.bulges[i] ?? 0) !== 0) return true;
  }
  return false;
}

/** One span endpoint of the polyline being decomposed, in its own 2D frame. */
interface ExplodeSpanVertex {
  readonly x: number;
  readonly y: number;
  /** The bulge of the span LEAVING this vertex — already `?? 0`-resolved. */
  readonly bulge: number;
}

/** Identity + shell fields every emitted piece carries. */
interface ExplodePieceShell {
  readonly spaceId: string;
  readonly layer: string;
  readonly color: BlockChildEntity["color"];
  readonly linetype: string;
  readonly lineweight: BlockChildEntity["lineweight"];
  readonly transparency: BlockChildEntity["transparency"];
  readonly linetypeScale?: number | undefined;
}

/** What {@link explodeBulgedSpans} needs from either source kind. */
interface ExplodeSource {
  /** Span endpoints in traversal order; the CLOSING span is walked explicitly. */
  readonly vertices: readonly ExplodeSpanVertex[];
  readonly closed: boolean;
  /** The source plane — every arc piece's LOCAL centre z (see below). */
  readonly elevation: number;
  /**
   * The child's OWN OCS plane, when its group 210 is not the WCS default
   * (@codex round 18, FINDING A) — `null` for every default-extrusion source
   * and for the LWPOLYLINE arm, which deliberately consumes no group 210 (see
   * `./blockChildBandFrame.ts`' module doc for why).
   *
   * {@link vertices} are the authored 2D coordinates IN this plane, so the arc
   * is built there; the finished ELLIPSE piece is then lifted OUT of it, which
   * is what lets one piece carry the tilt its own `extrusion` names.
   */
  readonly plane?: PolylinePlane | null;
  /**
   * The NORMALIZED group-210 normal the lifted arc pieces carry — the `az` of
   * the very basis {@link plane} lifts through, never a second normalization of
   * the raw extrusion. Present exactly when {@link plane} is.
   */
  readonly extrusion?: Vec3;
  readonly shell: ExplodePieceShell;
  /**
   * Rebuild one maximal STRAIGHT run as a piece of the SOURCE kind. `indices`
   * address {@link vertices} and may WRAP (a closed source's final run ends at
   * index 0), so the callback is handed indices rather than a slice.
   */
  straightRun(indices: readonly number[], pieceId: string): BlockChildEntity;
}

/** The shell fields, read off any block child that carries them. */
function pieceShellOf(child: BlockChildEntity): ExplodePieceShell {
  return {
    spaceId: child.spaceId,
    layer: child.layer,
    color: child.color,
    linetype: child.linetype,
    lineweight: child.lineweight,
    transparency: child.transparency,
    ...(child.linetypeScale !== undefined
      ? { linetypeScale: child.linetypeScale }
      : {}),
  };
}

/**
 * THE span walk — maximal straight runs plus one curved piece per bulged span.
 *
 * Shared by both source kinds (module header). Piece ids are
 * `<baseId>-run<N>` / `<baseId>-arc<N>`, numbered by emission order, and the
 * caller transforms each returned piece through `transformBlockChild` with that
 * id.
 *
 * With `emitCircularArcs` (uniform NON-mirrored insert scale, @codex #431
 * round-5) a bulged span becomes a circle-arc ARC child in CCW degree range —
 * the Arc arm's `radius·|sx|` + `angle+rot` transform is EXACT there. Otherwise
 * it becomes a circle-arc Ellipse child (`ratio 1`, CCW params) that the exact
 * conjugate-diameter transform maps through mirror / non-uniform (both from the
 * shared `bulgeArc` one-truth).
 *
 * A TILTED source ({@link ExplodeSource.plane}) always takes the Ellipse form:
 * the Arc arm maps a circular endpoint ANGLE through the scalar table
 * `mapUniformArcAngleDeg`, which speaks about the WCS XY plane and carries no
 * OCS frame, whereas the Ellipse form states its plane in its own axes and
 * group 210 — and is exact under a uniform scale too, so nothing is traded.
 *
 * The source `elevation` IS carried: each arc piece's local centre gets
 * `z = elevation` (@codex #431 round-2), so the block z-collapse lifts arc
 * pieces to EXACTLY the plane of their sibling straight runs
 * (`((elevation − basePoint.z)·scaleZ + insertion.z)`) — one source polyline can
 * no longer tear across z.
 */
function explodeBulgedSpans(
  src: ExplodeSource,
  baseId: string,
  emitCircularArcs: boolean,
): BlockChildEntity[] {
  const out: BlockChildEntity[] = [];
  const verts = src.vertices;
  let run: number[] = [];
  const flushRun = (): void => {
    if (run.length >= 2) out.push(src.straightRun(run, `${baseId}-run${out.length}`));
    run = [];
  };

  const segCount = src.closed ? verts.length : verts.length - 1;
  for (let i = 0; i < segCount; i++) {
    const ai = i;
    const bi = (i + 1) % verts.length;
    const a = verts[ai]!;
    const b = verts[bi]!;
    // `bulgeArc` is the shared one-truth (drawing-model): null ⇒ the segment
    // is effectively straight (|bulge| ≈ 0 or degenerate chord).
    const arc = bulgeArc(a, b, a.bulge);
    if (!arc) {
      if (run.length === 0) run.push(ai);
      run.push(bi);
      continue;
    }
    flushRun();
    // CCW range for the circle arc: `signedSweep` < 0 means the point set runs
    // CW from `startAngle`, i.e. CCW-ascending from `startAngle + signedSweep`
    // back up to `startAngle`. The swap is the ONE-TRUTH `bulgeArcCcwAngles`
    // (`drawing-model/bulgeArc.ts`) — shared with the top-level heavy POLYLINE
    // explode (`command-engine/explodeHeavyPolyline.ts`), so a bulged span can
    // never be drawn on one side of its chord and exploded to the other.
    const { startRad: ccwStartRad, endRad: ccwEndRad } = bulgeArcCcwAngles(arc);
    // Common identity/shell fields for either piece kind. The LOCAL centre z
    // carries the source elevation (re-attached AFTER the parse for the
    // Ellipse piece — its 2D `drawingPointSchema` strips it; the Arc schema
    // keeps the optional z through the parse).
    const pieceShell = { id: `${baseId}-arc${out.length}`, ...src.shell };
    // The arc's centre and its major-axis VECTOR, lifted out of the source
    // plane when there is one (@codex round 18, FINDING A). `toWorld` is
    // linear — `ocsToWcs` applies no translation — so mapping `(radius, 0, 0)`
    // through it IS `radius · ax`, the image of the arc's own +X semi-axis,
    // and `extrusion × majorAxis` then recovers `radius · ay` for the minor
    // one, so the CCW parameters below still measure the SAME in-plane angle
    // they were computed as. Without a plane both are the authored numbers
    // verbatim — the byte-identical default path.
    const plane = src.plane ?? null;
    const centre = plane
      ? plane.toWorld({ x: arc.cx, y: arc.cy, z: src.elevation })
      : { x: arc.cx, y: arc.cy, z: src.elevation };
    const major = plane
      ? plane.toWorld({ x: arc.radius, y: 0, z: 0 })
      : { x: arc.radius, y: 0 };
    if (emitCircularArcs && !plane) {
      // Uniform non-mirrored — exact circle transform via the Arc arm
      // (degrees, CCW like `arcEntitySchema` / the scene arc geometry).
      out.push(
        arcEntitySchema.parse({
          type: "Arc",
          ...pieceShell,
          center: centre,
          radius: arc.radius,
          startAngle: (ccwStartRad * 180) / Math.PI,
          endAngle: (ccwEndRad * 180) / Math.PI,
        }),
      );
    } else {
      const arcPiece = ellipseEntitySchema.parse({
        type: "Ellipse",
        ...pieceShell,
        center: { x: centre.x, y: centre.y },
        majorAxisEndpoint: major,
        ratio: 1,
        startParam: ccwStartRad,
        endParam: ccwEndRad,
        // Only when the source is tilted — a default-extrusion piece omits the
        // key entirely and keeps the schema's own (0, 0, 1).
        ...(plane && src.extrusion ? { extrusion: src.extrusion } : {}),
      });
      out.push({ ...arcPiece, center: centre });
    }
  }
  flushRun();
  return out;
}

/**
 * Decompose a bulged LWPOLYLINE into LOCAL-space children whose kinds transform
 * EXACTLY and render as true curves (ezdxf explode semantics: LWPOLYLINE →
 * Line/Arc virtual entities; a circular arc under non-uniform scale → ellipse).
 * Straight runs come back as LWPOLYLINE pieces (bulge 0, per-vertex widths
 * preserved, `closed: false` — the closing segment of a closed source is walked
 * explicitly, so the point set is complete); the curved spans ride
 * {@link explodeBulgedSpans}.
 *
 * Documented limitation: a per-vertex width taper across a bulged segment is
 * not representable on the Arc/Ellipse piece (widths survive on the straight
 * runs). TOP-LEVEL bulged polylines remain the renderer-wide chord gap — owner:
 * OSNAP-INTERSECT-EMIT-S2 (`docs/test-gaps/sub-osnap.md` — batcher tessellation
 * + arc AABB + arc snap must land together); once it lands, the uniform explode
 * merely becomes redundant (its output is exact either way).
 */
export function explodeBulgedLwpolylineChild(
  child: LwpolylineEntity,
  baseId: string,
  emitCircularArcs: boolean,
): BlockChildEntity[] {
  const verts = child.vertices;
  // Same finite-or-0 clamp `lwpolylineFromEntity` applies to the field.
  const elevation = Number.isFinite(child.elevation ?? 0)
    ? (child.elevation ?? 0)
    : 0;
  return explodeBulgedSpans(
    {
      vertices: verts.map((v) => ({ x: v.x, y: v.y, bulge: v.bulge ?? 0 })),
      closed: child.closed,
      elevation,
      shell: pieceShellOf(child),
      straightRun: (indices, pieceId) => ({
        ...child,
        id: pieceId,
        vertices: indices.map((i) => ({ ...verts[i]!, bulge: 0 })),
        closed: false,
      }),
    },
    baseId,
    emitCircularArcs,
  );
}

/**
 * ONE bulged heavy 2D `POLYLINE` run, REBUILT as a bulge-free polyline whose
 * vertices are its own arcs already walked in its own OCS plane (@codex round
 * 18, FINDING B).
 *
 * # Why a rebuild rather than a curved PIECE
 *
 * The span this run carries is not only a centreline. Its groups 40/41 draw a
 * width BAND and its group 39 draws a ruled SWEEP, and `SceneGeometry.ellipse`
 * carries neither field — so the `Ellipse` piece the other arm emits would drop
 * both, which is exactly the half-band / half-hairline entity FINDING B names.
 * The heavy `POLYLINE` IS the kind whose adapter paints both, so the answer is
 * to keep the kind and remove the only thing about it that was wrong: the
 * `bulge`, whose circular arc through the mapped endpoints is not the affine
 * image of the authored one.
 *
 * Rebuilding the WHOLE run in one piece (rather than one piece per span) is
 * load-bearing too: `widePolylineOutlineLoops` MITRES the joints between
 * consecutive spans of one entity, and splitting the run into separate
 * renderables would butt-cap every joint the split creates.
 *
 * # Why this is the SAME walk the band builder would have done
 *
 * The sub-division is `polylineBandSegments` — `cad-core`'s one span walk, the
 * one `widePolylineBandLoops`, the batcher, the pick, the snaps and the vector
 * plot all share — so the sub-segments ARE the ones the band builder produces
 * for this run, and `seg.wa`/`seg.wb` are its own interpolation of the group
 * 40 → 41 taper across each of them (there is no second copy of that lerp
 * here). The band built from the rebuilt vertices is therefore the band that
 * was already being drawn, while the CENTRELINE — which `polylineCentrelineSamples`
 * would otherwise re-derive from the bulge in the wrong frame — is now the
 * walked points themselves, and the pick, the snaps and the plot follow them.
 *
 * The walk happens in the LOCAL points the caller resolved through
 * `polylinePlane(child.extrusion)`, i.e. in the plane the bulge is authored in,
 * so a tilted child bows the right way (FINDING A); the rebuilt records are
 * written back as the OCS coordinates the kind stores, and the piece keeps the
 * child's own group 210, group 39 and entity-level default widths.
 *
 * Group-70 CURVE_FIT / SPLINE_FIT are CLEARED on the piece. Both bits change
 * which records `heavyPolylineVisibleIndices` treats as the drawn run — a
 * spline-fit entity draws only its bit-8 generated vertices — and the rebuilt
 * list IS the drawn run already, fully resolved, so leaving either bit set
 * would make the piece filter its own samples away.
 */
function walkedHeavyPolylineRun(
  child: PolylineEntity,
  run: HeavyPolylineRun,
  localPoints: readonly Vec3[],
  widths: readonly PolylineWidth[],
  pieceId: string,
): PolylineEntity {
  const carriesBand = hasPolylineWidth(widths);
  // The SAME shared walk, read twice: `polylineSpanSubSegments` says WHERE each
  // sub-segment sits (`span` + `ua`, so an authored vertex can be recognised)
  // and `polylineBandSegments` — which is a `.map` over exactly that walk, so
  // the two lists are positionally aligned by construction — carries the group
  // 40 → 41 taper interpolated onto it. Neither the subdivision nor the taper
  // lerp is restated here.
  const segments = polylineSpanSubSegments(localPoints, run.closed, run.bulges);
  const taper = polylineBandSegments(
    localPoints,
    run.closed,
    run.bulges,
    widths,
  );
  const recordAt = (k: number): PolylineVertexRecord | undefined =>
    child.vertices[run.indices[k] ?? -1];
  // A sample that sits ON an authored vertex (`ua === 0` — the first
  // sub-segment of its span) is rebuilt FROM that vertex's record, so its
  // handle, its group-50 tangent, its vertex id and its XDATA survive exactly
  // as they do on the other arm's straight runs. An INTERIOR sample of a bowed
  // span is a point the file never named, so it gets a fresh record.
  const vertices: PolylineVertexRecord[] = segments.map((seg, i) => {
    const authored = seg.ua === 0 ? recordAt(seg.span) : undefined;
    const base: PolylineVertexRecord = authored ?? {
      handle: "",
      location: { x: 0, y: 0, z: 0 },
    };
    const pair = taper[i];
    return {
      ...base,
      location: { ...base.location, x: seg.a.x, y: seg.a.y },
      bulge: 0,
      ...(carriesBand && pair
        ? { startWidth: pair.wa, endWidth: pair.wb }
        : {}),
    };
  });
  // An OPEN run has to carry its own final point; a CLOSED one's wrap span IS
  // the last sub-segment, whose `b` is the first vertex again.
  const tail = segments[segments.length - 1];
  if (!run.closed && tail) {
    const last = recordAt(run.indices.length - 1);
    const base: PolylineVertexRecord = last ?? {
      handle: "",
      location: { x: 0, y: 0, z: 0 },
    };
    vertices.push({
      ...base,
      location: { ...base.location, x: tail.b.x, y: tail.b.y },
      bulge: 0,
    });
  }
  return {
    ...child,
    id: pieceId,
    flags:
      child.flags &
      ~(DXF_POLYLINE_FLAG.CURVE_FIT | DXF_POLYLINE_FLAG.SPLINE_FIT),
    vertices,
  };
}

/**
 * Decompose a bulged heavy 2D `POLYLINE` block child (@codex round 17, FINDING
 * B; @codex round 18, FINDINGS A + B). Only ever called when
 * {@link heavyPolylineNeedsBulgeExplode} says so, i.e. under a NON-SIMILARITY
 * insert — where the affine image of the authored circle is an ELLIPSE and the
 * carried bulge would re-draw a circle through the mapped endpoints instead.
 *
 * Coordinates come from the shared topology — `heavyPolylineRuns` for which
 * records are visible geometry and which spans are curved,
 * `heavyPolylineWcsResolver` for the points themselves, the same two calls
 * `polylineFromEntity` and the picker read. So a spline-fit polyline's control
 * FRAME cannot enter the walk, the per-span bulges follow the FILTERED record
 * order rather than the raw index, and the non-finite clamp is the resolver's
 * own rather than a second copy of it. Those WCS points are then projected back
 * into the child's authored plane by `polylinePlane(child.extrusion)` — the one
 * exact OCS round trip, a no-op that returns `null` for the overwhelmingly
 * common default extrusion — because a bulge is measured in that plane and
 * nowhere else (@codex round 18, FINDING A).
 *
 * WHICH decomposition then runs is decided by what the run CARRIES (module
 * header): a run that declares a width band, or an entity that declares a
 * group-39 sweep, is rebuilt whole by {@link walkedHeavyPolylineRun} so both
 * survive; anything else takes the shared {@link explodeBulgedSpans} and its
 * true `Ellipse` curves. `emitCircularArcs` is `false` by construction — the
 * circular Arc form is exact only under a uniform non-mirrored scale, which
 * this arm never sees.
 *
 * A straight run is rebuilt as a heavy POLYLINE piece over the ORIGINAL vertex
 * RECORDS — so their widths (40/41), curve-fit tangents (50) and per-record
 * handles survive — with group 70 bit 1 cleared, because every run is emitted
 * open (a closed source's wrap-around span is walked explicitly).
 */
export function explodeBulgedHeavyPolylineChild(
  child: PolylineEntity,
  baseId: string,
): BlockChildEntity[] {
  const run = heavyPolylineRuns(child)[0];
  if (!run) return [];
  const indices = run.indices;
  const wcsOf = heavyPolylineWcsResolver(child);
  const plane = polylinePlane(child.extrusion);
  const localPoints = indices.map((record) => {
    const p = wcsOf(record);
    return plane ? plane.toLocal(p) : p;
  });
  const widths = heavyPolylineWidths(child, indices);
  // Does anything but the centreline ride these spans? `hasPolylineWidth` is
  // the one predicate for "is this drawn as a BAND", read over the run's OWN
  // visible indices; `thicknessSweepVector` is the one reading of "does group
  // 39 displace this entity at all", including its `1e-9` gate — the same call
  // `./blockChildThicknessSweep.ts` resolves the world vector with.
  const carriesBand = hasPolylineWidth(widths);
  const carriesSweep =
    thicknessSweepVector(child.thickness, child.extrusion) !== null;
  if (carriesBand || carriesSweep) {
    return localPoints.length >= 2
      ? [walkedHeavyPolylineRun(child, run, localPoints, widths, baseId)]
      : [{ ...child, id: baseId }];
  }
  return explodeBulgedSpans(
    {
      vertices: localPoints.map((p, k) => ({
        x: p.x,
        y: p.y,
        bulge: run.bulges[k] ?? 0,
      })),
      closed: run.closed,
      elevation: localPoints[0]?.z ?? 0,
      plane,
      // `computeOcsAxes` is the SAME Arbitrary-Axis basis `polylinePlane` lifts
      // through, so the piece's group 210 and its axes cannot disagree.
      ...(plane ? { extrusion: computeOcsAxes(child.extrusion).az } : {}),
      shell: pieceShellOf(child),
      straightRun: (ks, pieceId) => ({
        ...child,
        id: pieceId,
        flags: child.flags & ~DXF_POLYLINE_FLAG.CLOSED,
        vertices: ks.map((k) => ({
          ...child.vertices[indices[k]!]!,
          bulge: 0,
        })),
      }),
    },
    baseId,
    false,
  );
}
